// src/lib/resources/data/generators/buyerPersonaGenerator.ts

import { 
  ToolType, 
  ToolCategories, 
  DifficultyLevel
} from '~/lib/resources/types/shared/enums'
import type { Generator } from '~/lib/resources/types/tools'

export const buyerPersonaGenerator: Generator = {
  id: 'buyer-persona-generator',
  title: 'Buyer Persona Generator',
  canonicalTitle: 'Buyer Persona Generator',
  description: 'Create comprehensive, data-driven buyer personas to optimize your paid media targeting, improve ROAS across advertising platforms, and develop more effective marketing strategies.',
  resourceType: 'tool',
  toolType: ToolType.GENERATOR,
  toolCategory: ToolCategories.AUDIENCE,
  slug: 'buyer-persona-interactive-generator',
  category: 'Audience Tools',
  featured: true,
  lastUpdated: '2026-07-11T00:00:00+00:00',
  estimatedTime: '10-15 min',
  difficulty: DifficultyLevel.INTERMEDIATE,
  
  // Images
  images: {
    hero: {
      url: '/images/resources/tools/generators/adsights-buyer-persona-interactive-generator-audience-tools-marketing-tools-hero-image_AR-16x9_2400x1350_2x.jpg',
      width: 2400,
      height: 1350,
      alt: 'Interactive Buyer Persona Generator tool illustration showing demographic, psychographic, and behavioral data visualization for digital marketing campaigns'
    },
    thumbnail: {
      url: '/images/resources/tools/generators/adsights-buyer-persona-interactive-generator-audience-tools-marketing-tools-hero-image_AR-16x9_1200x675_1x.jpg',
      width: 1200,
      height: 675,
      alt: 'Interactive Buyer Persona Generator tool illustration showing demographic, psychographic, and behavioral data visualization for digital marketing campaigns'
    },
    og: {
      url: '/images/resources/tools/generators/adsights-buyer-persona-interactive-generator-audience-tools-marketing-tools-og-image_AR-1x1.91_2400x1260_2x.jpg',
      width: 2400,
      height: 1260,
      alt: 'Interactive Buyer Persona Generator tool illustration showing demographic, psychographic, and behavioral data visualization for digital marketing campaigns'
    }
  },
  
  // Related resources
  relatedTools: [
    'ab-test-statistical-significance-calculator', // A/B Testing Calculator
    'roas-calculator', // ROAS Calculator
    'creative-quality-grader', // Creative Quality Grader
    'marketing-funnel-stage-knowledge-check', // Marketing Funnel Stage Knowledge Check
    'media-buyer-skills-assessment', // Media Buyer Skills Assessment
  ],
  
  // Metadata
  metadata: {
    title: 'Buyer Persona Generator | Free Interactive Marketing Tool',
    description: 'Generate comprehensive buyer personas to improve your digital marketing targeting, messaging, and conversion rates. This free interactive tool helps marketers identify ideal customer profiles, psychographic traits, pain points, and buying behaviors for more effective campaigns.',
    keywords: [
      'buyer persona generator',
      'marketing persona tool',
      'marketing persona creator',
      'customer profile tool',
      'audience targeting template',
      'digital marketing persona',
      'ad targeting persona',
      'audience research tool',
      'interactive buyer persona tool',
      'audience targeting',
      'customer profile creator',
      'target audience builder',
      'demographic targeting',
      'marketing segmentation',
      'user persona template',
      'B2B persona generator',
      'B2C persona generator',
      'ideal customer profile',
      'audience research',
      'psychographic profiling',
      'customer persona builder',
      'digital marketing personas',
      'customer segmentation',
      'target audience',
      'psychographics',
      'behavioral targeting',
      'user persona',
      // Verbatim-adjacent Bing AI grounding query (seo_opportunities ledger,
      // bing_ai_low_citation_share: "comprehensive buyer persona creation tools").
      'buyer persona creation tool',
      'comprehensive buyer persona'
    ],
    dateCreated: '2025-03-09T12:00:00Z',
    dateModified: '2026-07-11T00:00:00+00:00',
    canonicalURL: '/resources/tools/generators/buyer-persona-interactive-generator'
  },

  // Structured data (Schema.org)
  teaches: 'Buyer Persona Development, Customer Segmentation, Target Audience Analysis, Marketing Strategy, Customer Journey Mapping',
  assesses: 'Marketing Persona Creation, Audience Research, Customer Understanding, Market Segmentation',
  applicationCategory: 'BusinessApplication',
  applicationSubCategory: 'Marketing Research & Planning Tool',
  featureList: [
    'Interactive buyer persona builder with customizable demographic profiles',
    'Psychographic trait analysis',
    'Behavioral pattern mapping',
    'Multi-platform audience targeting',
    'Real-time persona preview',
    'Export capabilities',
  ],
  interactivityType: 'active',
  softwareRequirements: 'Modern web browser with JavaScript enabled',
  browserRequirements: 'Chrome 80+, Firefox 75+, Safari 13.1+, or Edge 80+',
  
  // Stats
  stats: {
    views: 0,
    completions: 0,
    shares: 0
  },
  
  config: {
    steps: [
      // Step 1: Business Context
      {
        id: 'business-context',
        title: 'Your Business Context',
        description: 'Let\'s start by understanding your business context to tailor the persona to your needs.',
        fields: [
          {
            id: 'businessType',
            type: 'select',
            label: 'Your Business Type',
            description: 'What type of business do YOU operate? This will help customize the persona template.',
            validation: {
              required: true
            },
            templateVar: 'businessType',
            affectsPreview: true,
            options: [
              { id: 'b2b', label: 'B2B (Business to Business)', value: 'B2B' },
              { id: 'b2c', label: 'B2C (Business to Consumer)', value: 'B2C' },
              { id: 'd2c', label: 'D2C (Direct to Consumer)', value: 'D2C' },
              { id: 'b2g', label: 'B2G (Business to Government)', value: 'B2G' },
              { id: 'marketplace', label: 'Marketplace', value: 'Marketplace' }
            ]
          },
          {
            id: 'companyName',
            type: 'text',
            label: 'Your Company Name',
            description: 'What is the name of your company? (This will be used for customizing the output)',
            validation: {
              required: false
            },
            templateVar: 'companyName',
            affectsPreview: true
          }
        ],
        template: `
          <div class="business-context">
            <div class="context-detail">
              <span class="label">Business Type:</span> {{businessType}}
            </div>
            {{#if companyName}}
            <div class="context-detail">
              <span class="label">Company:</span> {{companyName}}
            </div>
            {{/if}}
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="business-context-preview">
              <p class="context-type">Creating a {{businessType}} Persona</p>
              {{#if companyName}}<p class="context-company">For: {{companyName}}</p>{{/if}}
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 2: Basic Information
      {
        id: 'basic-info',
        title: 'Basic Information',
        description: 'Define who this buyer persona represents.',
        fields: [
          {
            id: 'personaName',
            type: 'text',
            label: 'Persona Name',
            description: 'Give your persona a name that reflects their role or characteristics.',
            placeholder: 'e.g., Marketing Manager Mary',
            validation: {
              required: true
            },
            templateVar: 'personaName',
            affectsPreview: true
          },
          {
            id: 'industry',
            type: 'select',
            label: 'Industry',
            description: 'Select the industry this persona works in.',
            validation: {
              required: true
            },
            templateVar: 'industry',
            affectsPreview: true,
            options: [
              { id: 'ecommerce', label: 'E-commerce', value: 'ecommerce' },
              { id: 'saas', label: 'SaaS/Technology', value: 'saas' },
              { id: 'finance', label: 'Finance/Banking', value: 'finance' },
              { id: 'healthcare', label: 'Healthcare', value: 'healthcare' },
              { id: 'education', label: 'Education', value: 'education' },
              { id: 'retail', label: 'Retail', value: 'retail' },
              { id: 'hospitality', label: 'Hospitality/Travel', value: 'hospitality' },
              { id: 'manufacturing', label: 'Manufacturing', value: 'manufacturing' },
              { id: 'professional-services', label: 'Professional Services', value: 'professional-services' },
              { id: 'nonprofit', label: 'Nonprofit', value: 'nonprofit' },
              { id: 'other', label: 'Other', value: 'other' }
            ]
          },
          {
            id: 'jobTitleB2B',
            type: 'text',
            label: 'Job Title',
            description: 'What position does this persona hold in their organization?',
            placeholder: 'e.g., Marketing Director, IT Manager, Purchasing Agent',
            templateVar: 'jobTitle',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'equals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'equals',
              value: 'B2B'
            }
          },
          {
            id: 'jobTitleB2C',
            type: 'text',
            label: 'Occupation/Role',
            description: 'What is this persona\'s occupation or life role?',
            placeholder: 'e.g., Parent, Student, Professional, Retiree',
            templateVar: 'jobTitle',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'notEquals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'notEquals',
              value: 'B2B'
            }
          },
          {
            id: 'responsibilitiesB2B',
            type: 'markdown',
            label: 'Key Responsibilities',
            description: 'What are their main job responsibilities?',
            placeholder: 'e.g., Managing marketing budget, overseeing campaign performance, reporting to executives',
            templateVar: 'responsibilities',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'equals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'equals',
              value: 'B2B'
            }
          },
          {
            id: 'responsibilitiesB2C',
            type: 'markdown',
            label: 'Daily Activities',
            description: 'What are their typical daily activities and responsibilities?',
            placeholder: 'e.g., Caring for family, managing household, pursuing education, recreational activities',
            templateVar: 'responsibilities',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'notEquals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'notEquals',
              value: 'B2B'
            }
          },
          {
            id: 'b2bDecisionAuthority',
            type: 'select',
            label: 'Purchase Decision Role',
            description: 'What level of purchasing authority does this persona have in their organization?',
            templateVar: 'decisionAuthority',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'equals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'equals',
              value: 'B2B'
            },
            options: [
              { id: 'final', label: 'Final Decision Maker', value: 'Final Decision Maker' },
              { id: 'significant', label: 'Significant Influence', value: 'Significant Influence' },
              { id: 'some', label: 'Some Influence', value: 'Some Influence' },
              { id: 'research', label: 'Researcher/Information Gatherer', value: 'Researcher/Information Gatherer' }
            ]
          },
          {
            id: 'b2cDecisionAuthority',
            type: 'select',
            label: 'Purchase Decision Role',
            description: 'How does this persona make purchasing decisions?',
            templateVar: 'decisionAuthority',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'notEquals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'notEquals',
              value: 'B2B'
            },
            options: [
              { id: 'primary', label: 'Primary Decision Maker', value: 'Primary Decision Maker' },
              { id: 'family', label: 'Joint Family Decision Maker', value: 'Joint Family Decision Maker' },
              { id: 'influenced', label: 'Influenced by Others', value: 'Influenced by Others' },
              { id: 'researcher', label: 'Researcher Before Purchase', value: 'Researcher Before Purchase' }
            ]
          }
        ],
        template: `
          <div class="buyer-persona-basic">
            <h2>{{personaName}}</h2>
            <div class="persona-detail">
              <span class="label">Industry:</span> {{industry}}
            </div>
            <div class="persona-detail">
              <span class="label">Business Type:</span> {{businessType}}
            </div>
            <div class="persona-detail">
              <span class="label">Job Title:</span> {{jobTitle}}
            </div>
            <div class="persona-detail">
              <span class="label">Decision Authority:</span> {{decisionAuthority}}
            </div>
            <div class="persona-detail">
              <span class="label">Key Responsibilities:</span>
              <p>{{responsibilities}}</p>
            </div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="persona-preview-card">
              <h3>{{personaName}}</h3>
              <p class="persona-preview-role">{{jobTitle}} at {{businessType}} {{industry}} Company</p>
              <p class="persona-preview-authority">{{decisionAuthority}}</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 2: Demographics
      {
        id: 'demographics',
        title: 'Demographics',
        description: 'Define the demographic characteristics of your buyer persona.',
        fields: [
          {
            id: 'ageRange',
            type: 'select',
            label: 'Age Range',
            description: 'What is the typical age range of this persona?',
            validation: {
              required: true
            },
            templateVar: 'ageRange',
            affectsPreview: true,
            options: [
              { id: '18-24', label: '18-24', value: '18-24' },
              { id: '25-34', label: '25-34', value: '25-34' },
              { id: '35-44', label: '35-44', value: '35-44' },
              { id: '45-54', label: '45-54', value: '45-54' },
              { id: '55-64', label: '55-64', value: '55-64' },
              { id: '65+', label: '65+', value: '65+' }
            ]
          },
          {
            id: 'gender',
            type: 'select',
            label: 'Gender',
            description: 'What is the typical gender of this persona?',
            validation: {
              required: true
            },
            templateVar: 'gender',
            affectsPreview: true,
            options: [
              { id: 'male', label: 'Male', value: 'Male' },
              { id: 'female', label: 'Female', value: 'Female' },
              { id: 'diverse', label: 'Diverse', value: 'Diverse' },
              { id: 'not-specified', label: 'Not Specified', value: 'Not Specified' }
            ]
          },
          {
            id: 'income',
            type: 'select',
            label: 'Income Level',
            description: 'What is the typical income level of this persona?',
            validation: {
              required: true
            },
            templateVar: 'income',
            affectsPreview: true,
            options: [
              { id: 'under-30k', label: 'Under $30,000', value: 'Under $30,000' },
              { id: '30k-50k', label: '$30,000 - $50,000', value: '$30,000 - $50,000' },
              { id: '50k-75k', label: '$50,000 - $75,000', value: '$50,000 - $75,000' },
              { id: '75k-100k', label: '$75,000 - $100,000', value: '$75,000 - $100,000' },
              { id: '100k-150k', label: '$100,000 - $150,000', value: '$100,000 - $150,000' },
              { id: '150k+', label: '$150,000+', value: '$150,000+' }
            ]
          },
          {
            id: 'education',
            type: 'select',
            label: 'Education Level',
            description: 'What is the typical education level of this persona?',
            validation: {
              required: true
            },
            templateVar: 'education',
            affectsPreview: true,
            options: [
              { id: 'high-school', label: 'High School', value: 'High School' },
              { id: 'associate', label: 'Associate Degree', value: 'Associate Degree' },
              { id: 'bachelors', label: 'Bachelor\'s Degree', value: 'Bachelor\'s Degree' },
              { id: 'masters', label: 'Master\'s Degree', value: 'Master\'s Degree' },
              { id: 'doctorate', label: 'Doctorate', value: 'Doctorate' },
              { id: 'professional', label: 'Professional Degree', value: 'Professional Degree' }
            ]
          },
          {
            id: 'location',
            type: 'text',
            label: 'Geographic Location',
            description: 'Where is this persona typically located?',
            placeholder: 'e.g., Urban areas in the United States, Europe, Global',
            validation: {
              required: false
            },
            templateVar: 'location',
            affectsPreview: true
          },
          {
            id: 'familyStatus',
            type: 'select',
            label: 'Family Status',
            description: 'What is the typical family status of this persona?',
            validation: {
              required: false
            },
            templateVar: 'familyStatus',
            affectsPreview: true,
            options: [
              { id: 'single', label: 'Single', value: 'Single' },
              { id: 'married', label: 'Married', value: 'Married' },
              { id: 'married-children', label: 'Married with Children', value: 'Married with Children' },
              { id: 'single-parent', label: 'Single Parent', value: 'Single Parent' },
              { id: 'divorced', label: 'Divorced', value: 'Divorced' },
              { id: 'widowed', label: 'Widowed', value: 'Widowed' }
            ]
          }
        ],
        template: `
          <div class="buyer-persona-demographics">
            <h3>Demographics</h3>
            <div class="persona-detail">
              <span class="label">Age Range:</span> {{ageRange}}
            </div>
            <div class="persona-detail">
              <span class="label">Gender:</span> {{gender}}
            </div>
            <div class="persona-detail">
              <span class="label">Income Level:</span> {{income}}
            </div>
            <div class="persona-detail">
              <span class="label">Education:</span> {{education}}
            </div>
            <div class="persona-detail">
              <span class="label">Location:</span> {{location}}
            </div>
            <div class="persona-detail">
              <span class="label">Family Status:</span> {{familyStatus}}
            </div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="persona-preview-demographics">
              <p class="persona-preview-age-gender">{{gender}}, {{ageRange}}</p>
              <p class="persona-preview-location">{{location}}</p>
              <p class="persona-preview-income">{{income}}</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 3: Psychographics
      {
        id: 'psychographics',
        title: 'Psychographics',
        description: 'Define the psychological characteristics, values, and motivations of your buyer persona.',
        fields: [
          {
            id: 'valuesB2B',
            type: 'multiselect',
            label: 'Professional Values',
            description: 'What core values drive this persona in their professional role?',
            templateVar: 'values',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'equals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'equals',
              value: 'B2B'
            },
            options: [
              { id: 'innovation', label: 'Innovation', value: 'Innovation' },
              { id: 'reliability', label: 'Reliability', value: 'Reliability' },
              { id: 'efficiency', label: 'Efficiency', value: 'Efficiency' },
              { id: 'quality', label: 'Quality', value: 'Quality' },
              { id: 'sustainability', label: 'Sustainability', value: 'Sustainability' },
              { id: 'costEffective', label: 'Cost-effectiveness', value: 'Cost-effectiveness' },
              { id: 'security', label: 'Security', value: 'Security' },
              { id: 'compliance', label: 'Compliance', value: 'Compliance' },
              { id: 'growth', label: 'Growth/Learning', value: 'Growth/Learning' },
              { id: 'integrity', label: 'Integrity', value: 'Integrity' },
              { id: 'collaboration', label: 'Collaboration', value: 'Collaboration' },
              { id: 'leadership', label: 'Leadership', value: 'Leadership' },
              { id: 'accountability', label: 'Accountability', value: 'Accountability' },
              { id: 'agility', label: 'Agility', value: 'Agility' },
              { id: 'datadriven', label: 'Data-driven decision making', value: 'Data-driven decision making' },
              { id: 'scalability', label: 'Scalability', value: 'Scalability' },
              { id: 'transparency', label: 'Transparency', value: 'Transparency' }
            ]
          },
          {
            id: 'valuesB2C',
            type: 'multiselect',
            label: 'Personal Values',
            description: 'What core values are most important to this persona in their personal life?',
            templateVar: 'values',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'notEquals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'notEquals',
              value: 'B2B'
            },
            options: [
              { id: 'family', label: 'Family', value: 'Family' },
              { id: 'convenience', label: 'Convenience', value: 'Convenience' },
              { id: 'quality', label: 'Quality', value: 'Quality' },
              { id: 'affordability', label: 'Affordability', value: 'Affordability' },
              { id: 'sustainability', label: 'Sustainability', value: 'Sustainability' },
              { id: 'authenticity', label: 'Authenticity', value: 'Authenticity' },
              { id: 'wellness', label: 'Wellness', value: 'Wellness' },
              { id: 'growth', label: 'Personal Growth', value: 'Personal Growth' },
              { id: 'community', label: 'Community', value: 'Community' },
              { id: 'experience', label: 'Experiences', value: 'Experiences' },
              { id: 'social', label: 'Social Connection', value: 'Social Connection' },
              { id: 'security', label: 'Security', value: 'Security' },
              { id: 'status', label: 'Status/Prestige', value: 'Status/Prestige' },
              { id: 'integrity', label: 'Integrity', value: 'Integrity' },
              { id: 'selfcare', label: 'Self-Care', value: 'Self-Care' },
              { id: 'worklifebalance', label: 'Work-Life Balance', value: 'Work-Life Balance' },
              { id: 'creativity', label: 'Creativity', value: 'Creativity' },
              { id: 'adventure', label: 'Adventure', value: 'Adventure' },
              { id: 'minimalism', label: 'Minimalism', value: 'Minimalism' },
              { id: 'luxury', label: 'Luxury', value: 'Luxury' }
            ]
          },
          {
            id: 'interests',
            type: 'multiselect',
            label: 'Interests',
            description: 'What topics or activities is this persona interested in?',
            validation: {
              required: false
            },
            templateVar: 'interests',
            affectsPreview: true,
            options: [
              { id: 'technology', label: 'Technology', value: 'Technology' },
              { id: 'business', label: 'Business', value: 'Business' },
              { id: 'finance', label: 'Finance', value: 'Finance' },
              { id: 'marketing', label: 'Marketing', value: 'Marketing' },
              { id: 'sales', label: 'Sales', value: 'Sales' },
              { id: 'travel', label: 'Travel', value: 'Travel' },
              { id: 'fitness', label: 'Fitness', value: 'Fitness' },
              { id: 'health', label: 'Health & Wellness', value: 'Health & Wellness' },
              { id: 'fashion', label: 'Fashion', value: 'Fashion' },
              { id: 'food', label: 'Food & Dining', value: 'Food & Dining' },
              { id: 'environment', label: 'Environment', value: 'Environment' },
              { id: 'education', label: 'Education', value: 'Education' },
              { id: 'arts', label: 'Arts & Culture', value: 'Arts & Culture' },
              { id: 'sports', label: 'Sports', value: 'Sports' },
              { id: 'entrepreneurship', label: 'Entrepreneurship', value: 'Entrepreneurship' }
            ]
          },
          {
            id: 'goalsB2B',
            type: 'markdown',
            label: 'Professional Goals',
            description: 'What are the primary business and career goals of this persona?',
            placeholder: 'e.g., Increase department ROI, reduce operational costs, advance to executive level',
            templateVar: 'goals',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'equals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'equals',
              value: 'B2B'
            }
          },
          {
            id: 'goalsB2C',
            type: 'markdown',
            label: 'Personal Goals',
            description: 'What are the primary personal and lifestyle goals of this persona?',
            placeholder: 'e.g., Achieve work-life balance, save for retirement, spend more time with family',
            templateVar: 'goals',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'notEquals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'notEquals',
              value: 'B2B'
            }
          },
          {
            id: 'challengesB2B',
            type: 'markdown',
            label: 'Professional Challenges',
            description: 'What business challenges does this persona face in their role?',
            placeholder: 'e.g., Limited budget, tight deadlines, difficult stakeholder management',
            templateVar: 'challenges',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'equals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'equals',
              value: 'B2B'
            }
          },
          {
            id: 'challengesB2C',
            type: 'markdown',
            label: 'Personal Challenges',
            description: 'What challenges does this persona face in their daily life?',
            placeholder: 'e.g., Time constraints, budget limitations, lack of information',
            templateVar: 'challenges',
            affectsPreview: true,
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'notEquals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'notEquals',
              value: 'B2B'
            }
          },
          {
            id: 'painPoints',
            type: 'markdown',
            label: 'Pain Points',
            description: 'What specific pain points frustrate this persona?',
            placeholder: 'e.g., Unreliable vendors, complex reporting requirements, lack of data integration',
            templateVar: 'painPoints',
            affectsPreview: true,
            validation: {
              required: false
            }
          }
        ],
        template: `
          <div class="buyer-persona-psychographics">
            <h3>Psychographics</h3>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Professional Values:{{else}}Personal Values:{{/if}}</span> {{#if businessType == 'B2B'}}{{valuesB2B}}{{else}}{{valuesB2C}}{{/if}}
            </div>
            <div class="persona-detail">
              <span class="label">Interests:</span> {{interests}}
            </div>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Professional Goals:{{else}}Personal Goals:{{/if}}</span>
              <div class="persona-markdown">{{#if businessType == 'B2B'}}{{goalsB2B}}{{else}}{{goalsB2C}}{{/if}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Professional Challenges:{{else}}Personal Challenges:{{/if}}</span>
              <div class="persona-markdown">{{#if businessType == 'B2B'}}{{challengesB2B}}{{else}}{{challengesB2C}}{{/if}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Pain Points:</span>
              <div class="persona-markdown">{{painPoints}}</div>
            </div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="persona-preview-psychographics">
              <p class="persona-preview-values">Values: {{#if businessType == 'B2B'}}{{valuesB2B}}{{else}}{{valuesB2C}}{{/if}}</p>
              <p class="persona-preview-challenges">Top challenges: {{#if businessType == 'B2B'}}{{challengesB2B}}{{else}}{{challengesB2C}}{{/if}}</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 4: Behavior & Buying Patterns
      {
        id: 'behavior',
        title: 'Behavior & Buying Patterns',
        description: 'Define how this persona researches, evaluates, and makes purchase decisions.',
        fields: [
          {
            id: 'researchMethods',
            type: 'multiselect',
            label: 'Research Methods',
            description: 'How does this persona typically research products or services?',
            validation: {
              required: true
            },
            templateVar: 'researchMethods',
            affectsPreview: true,
            options: [
              { id: 'search', label: 'Search Engines', value: 'Search Engines' },
              { id: 'social', label: 'Social Media', value: 'Social Media' },
              { id: 'reviews', label: 'Review Websites', value: 'Review Websites' },
              { id: 'peers', label: 'Peer Recommendations', value: 'Peer Recommendations' },
              { id: 'industry', label: 'Industry Publications', value: 'Industry Publications' },
              { id: 'events', label: 'Industry Events/Conferences', value: 'Industry Events/Conferences' },
              { id: 'salesrep', label: 'Speaking with Sales Reps', value: 'Speaking with Sales Reps' },
              { id: 'consultants', label: 'Consultants/Experts', value: 'Consultants/Experts' },
              { id: 'reports', label: 'Research Reports', value: 'Research Reports' },
              { id: 'demos', label: 'Product Demos', value: 'Product Demos' }
            ]
          },
          {
            id: 'purchaseFrequency',
            type: 'select',
            label: 'Purchase Frequency',
            description: 'How often does this persona make relevant purchases?',
            validation: {
              required: true
            },
            templateVar: 'purchaseFrequency',
            affectsPreview: true,
            options: [
              { id: 'weekly', label: 'Weekly', value: 'Weekly' },
              { id: 'monthly', label: 'Monthly', value: 'Monthly' },
              { id: 'quarterly', label: 'Quarterly', value: 'Quarterly' },
              { id: 'biannually', label: 'Biannually', value: 'Biannually' },
              { id: 'annually', label: 'Annually', value: 'Annually' },
              { id: 'lessoften', label: 'Less than Once a Year', value: 'Less than Once a Year' }
            ]
          },
          {
            id: 'decisionCriteriaB2B',
            type: 'multiselect',
            label: 'Decision Criteria',
            description: 'What factors are most important in their purchase decisions?',
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'equals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'equals',
              value: 'B2B'
            },
            templateVar: 'decisionCriteria',
            affectsPreview: true,
            options: [
              { id: 'price', label: 'Price', value: 'Price' },
              { id: 'quality', label: 'Quality', value: 'Quality' },
              { id: 'features', label: 'Features & Functionality', value: 'Features & Functionality' },
              { id: 'experience', label: 'User Experience', value: 'User Experience' },
              { id: 'roi', label: 'ROI/Value', value: 'ROI/Value' },
              { id: 'support', label: 'Customer Support', value: 'Customer Support' },
              { id: 'brand', label: 'Brand Reputation', value: 'Brand Reputation' },
              { id: 'implementation', label: 'Ease of Implementation', value: 'Ease of Implementation' },
              { id: 'scalability', label: 'Scalability', value: 'Scalability' },
              { id: 'security', label: 'Security/Compliance', value: 'Security/Compliance' },
              { id: 'integration', label: 'Integration Capabilities', value: 'Integration Capabilities' },
              { id: 'training', label: 'Training & Onboarding', value: 'Training & Onboarding' },
              { id: 'customization', label: 'Customization Options', value: 'Customization Options' },
              { id: 'ecosystem', label: 'Partner Ecosystem', value: 'Partner Ecosystem' },
              { id: 'innovation', label: 'Innovation Pipeline', value: 'Innovation Pipeline' },
              { id: 'contractterms', label: 'Contract Terms', value: 'Contract Terms' },
              { id: 'industryspecific', label: 'Industry-Specific Features', value: 'Industry-Specific Features' }
            ]
          },
          {
            id: 'decisionCriteriaB2C',
            type: 'multiselect',
            label: 'Decision Criteria',
            description: 'What factors are most important in their purchase decisions?',
            validation: {
              required: false,
              requiredIf: {
                field: 'businessType',
                operator: 'notEquals',
                value: 'B2B'
              }
            },
            showIf: {
              field: 'businessType',
              operator: 'notEquals',
              value: 'B2B'
            },
            templateVar: 'decisionCriteria',
            affectsPreview: true,
            options: [
              { id: 'price', label: 'Price', value: 'Price' },
              { id: 'quality', label: 'Quality', value: 'Quality' },
              { id: 'convenience', label: 'Convenience', value: 'Convenience' },
              { id: 'design', label: 'Design/Aesthetics', value: 'Design/Aesthetics' },
              { id: 'brand', label: 'Brand Reputation', value: 'Brand Reputation' },
              { id: 'reviews', label: 'Customer Reviews', value: 'Customer Reviews' },
              { id: 'recommendations', label: 'Personal Recommendations', value: 'Personal Recommendations' },
              { id: 'sustainability', label: 'Sustainability/Ethics', value: 'Sustainability/Ethics' },
              { id: 'warranty', label: 'Warranty/Guarantees', value: 'Warranty/Guarantees' },
              { id: 'shipping', label: 'Shipping Options', value: 'Shipping Options' },
              { id: 'returns', label: 'Return Policy', value: 'Return Policy' },
              { id: 'loyalty', label: 'Loyalty Programs', value: 'Loyalty Programs' },
              { id: 'trendiness', label: 'Trendiness', value: 'Trendiness' },
              { id: 'exclusivity', label: 'Exclusivity', value: 'Exclusivity' },
              { id: 'customization', label: 'Customization Options', value: 'Customization Options' },
              { id: 'easeofuse', label: 'Ease of Use', value: 'Ease of Use' },
              { id: 'availability', label: 'Availability', value: 'Availability' }
            ]
          },
          {
            id: 'communicationChannels',
            type: 'multiselect',
            label: 'Preferred Communication Channels',
            description: 'Which channels does this persona prefer for communication?',
            validation: {
              required: true
            },
            templateVar: 'communicationChannels',
            affectsPreview: true,
            options: [
              { id: 'email', label: 'Email', value: 'Email' },
              { id: 'phone', label: 'Phone', value: 'Phone' },
              { id: 'inperson', label: 'In-Person Meetings', value: 'In-Person Meetings' },
              { id: 'video', label: 'Video Calls', value: 'Video Calls' },
              { id: 'chat', label: 'Live Chat', value: 'Live Chat' },
              { id: 'social', label: 'Social Media', value: 'Social Media' },
              { id: 'messaging', label: 'Messaging Apps', value: 'Messaging Apps' },
              { id: 'mail', label: 'Direct Mail', value: 'Direct Mail' }
            ]
          },
          {
            id: 'brandLoyalty',
            type: 'markdown',
            label: 'Brand Loyalty Factors',
            description: 'What drives this persona\'s loyalty to brands or vendors?',
            placeholder: 'e.g., Consistent quality, responsive support, understanding their unique needs',
            validation: {
              required: false
            },
            templateVar: 'brandLoyalty',
            affectsPreview: true
          },
          {
            id: 'budgetConsciousness',
            type: 'select',
            label: 'Budget Consciousness',
            description: 'How budget-conscious is this persona?',
            validation: {
              required: true
            },
            templateVar: 'budgetConsciousness',
            affectsPreview: true,
            options: [
              { id: 'extremely', label: 'Extremely Budget-Conscious', value: 'Extremely Budget-Conscious' },
              { id: 'very', label: 'Very Budget-Conscious', value: 'Very Budget-Conscious' },
              { id: 'somewhat', label: 'Somewhat Budget-Conscious', value: 'Somewhat Budget-Conscious' },
              { id: 'slightly', label: 'Slightly Budget-Conscious', value: 'Slightly Budget-Conscious' },
              { id: 'not', label: 'Not Budget-Conscious', value: 'Not Budget-Conscious' }
            ]
          }
        ],
        template: `
          <div class="buyer-persona-behavior">
            <h3>Behavior & Buying Patterns</h3>
            <div class="persona-detail">
              <span class="label">Research Methods:</span> {{researchMethods}}
            </div>
            <div class="persona-detail">
              <span class="label">Purchase Frequency:</span> {{purchaseFrequency}}
            </div>
            <div class="persona-detail">
              <span class="label">Decision Criteria:</span> {{decisionCriteria}}
            </div>
            <div class="persona-detail">
              <span class="label">Preferred Communication:</span> {{communicationChannels}}
            </div>
            <div class="persona-detail">
              <span class="label">Brand Loyalty Factors:</span>
              <div class="persona-markdown">{{brandLoyalty}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Budget Consciousness:</span> {{budgetConsciousness}}
            </div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="persona-preview-behavior">
              <p class="persona-preview-research">Researches via: {{researchMethods}}</p>
              <p class="persona-preview-decisions">Decides based on: {{decisionCriteria}}</p>
              <p class="persona-preview-budget">{{budgetConsciousness}}</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 5: Digital Footprint
      {
        id: 'digital',
        title: 'Digital Footprint',
        description: 'Define the online presence and digital behavior of your buyer persona.',
        fields: [
          {
            id: 'platforms',
            type: 'multiselect',
            label: 'Preferred Platforms',
            description: 'Which online platforms does this persona use regularly?',
            validation: {
              required: false
            },
            templateVar: 'platforms',
            affectsPreview: true,
            options: [
              { id: 'facebook', label: 'Facebook', value: 'Facebook' },
              { id: 'instagram', label: 'Instagram', value: 'Instagram' },
              { id: 'linkedin', label: 'LinkedIn', value: 'LinkedIn' },
              { id: 'twitter', label: 'Twitter/X', value: 'Twitter/X' },
              { id: 'youtube', label: 'YouTube', value: 'YouTube' },
              { id: 'tiktok', label: 'TikTok', value: 'TikTok' },
              { id: 'pinterest', label: 'Pinterest', value: 'Pinterest' },
              { id: 'reddit', label: 'Reddit', value: 'Reddit' },
              { id: 'quora', label: 'Quora', value: 'Quora' },
              { id: 'medium', label: 'Medium', value: 'Medium' },
              { id: 'slack', label: 'Slack', value: 'Slack' },
              { id: 'discord', label: 'Discord', value: 'Discord' },
              { id: 'whatsapp', label: 'WhatsApp', value: 'WhatsApp' },
              { id: 'telegram', label: 'Telegram', value: 'Telegram' },
              { id: 'messenger', label: 'Messenger', value: 'Messenger' }
            ]
          },
          {
            id: 'devices',
            type: 'multiselect',
            label: 'Device Usage',
            description: 'Which devices does this persona use regularly?',
            validation: {
              required: false
            },
            templateVar: 'devices',
            affectsPreview: true,
            options: [
              { id: 'desktop', label: 'Desktop', value: 'Desktop' },
              { id: 'laptop', label: 'Laptop', value: 'Laptop' },
              { id: 'tablet', label: 'Tablet', value: 'Tablet' },
              { id: 'smartphone', label: 'Smartphone', value: 'Smartphone' },
              { id: 'smartwatch', label: 'Smartwatch', value: 'Smartwatch' },
              { id: 'smarttv', label: 'Smart TV', value: 'Smart TV' },
              { id: 'voice', label: 'Voice Assistant', value: 'Voice Assistant' }
            ]
          },
          {
            id: 'contentTypes',
            type: 'multiselect',
            label: 'Content Consumption',
            description: 'What types of content does this persona prefer?',
            validation: {
              required: true
            },
            templateVar: 'contentTypes',
            affectsPreview: true,
            options: [
              { id: 'blog', label: 'Blog Articles', value: 'Blog Articles' },
              { id: 'video', label: 'Videos', value: 'Videos' },
              { id: 'social', label: 'Social Media', value: 'Social Media' },
              { id: 'podcast', label: 'Podcasts', value: 'Podcasts' },
              { id: 'infographic', label: 'Infographics', value: 'Infographics' },
              { id: 'ebook', label: 'eBooks/Whitepapers', value: 'eBooks/Whitepapers' },
              { id: 'case', label: 'Case Studies', value: 'Case Studies' },
              { id: 'webinar', label: 'Webinars', value: 'Webinars' },
              { id: 'email', label: 'Email Newsletters', value: 'Email Newsletters' },
              { id: 'tv', label: 'TV/Streaming', value: 'TV/Streaming' },
              { id: 'review', label: 'Product Reviews', value: 'Product Reviews' },
              { id: 'tutorial', label: 'Tutorials/How-tos', value: 'Tutorials/How-tos' }
            ]
          },
          {
            id: 'onlineBehavior',
            type: 'markdown',
            label: 'Online Behavior Patterns',
            description: 'Describe how this persona typically behaves online.',
            placeholder: 'e.g., Primarily uses mobile during commute, researches products extensively before purchase, engages heavily with video content',
            validation: {
              required: false
            },
            templateVar: 'onlineBehavior',
            affectsPreview: true
          },
          {
            id: 'digitalInfluence',
            type: 'select',
            label: 'Digital Influence Level',
            description: 'How influential is this persona in digital spaces?',
            validation: {
              required: false
            },
            templateVar: 'digitalInfluence',
            affectsPreview: true,
            options: [
              { id: 'creator', label: 'Content Creator/Influencer', value: 'Content Creator/Influencer' },
              { id: 'active', label: 'Active Participant', value: 'Active Participant' },
              { id: 'occasional', label: 'Occasional Contributor', value: 'Occasional Contributor' },
              { id: 'lurker', label: 'Lurker/Observer', value: 'Lurker/Observer' },
              { id: 'minimal', label: 'Minimal Digital Presence', value: 'Minimal Digital Presence' }
            ]
          }
        ],
        template: `
          <div class="buyer-persona-digital">
            <h3>Digital Footprint</h3>
            <div class="persona-detail">
              <span class="label">Preferred Platforms:</span> {{platforms}}
            </div>
            <div class="persona-detail">
              <span class="label">Device Usage:</span> {{devices}}
            </div>
            <div class="persona-detail">
              <span class="label">Content Consumption:</span> {{contentTypes}}
            </div>
            <div class="persona-detail">
              <span class="label">Online Behavior:</span>
              <div class="persona-markdown">{{onlineBehavior}}</div>
            </div>
            {{#if digitalInfluence}}
            <div class="persona-detail">
              <span class="label">Digital Influence:</span> {{digitalInfluence}}
            </div>
            {{/if}}
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="persona-preview-digital">
              <p class="persona-preview-platforms">Found on: {{platforms}}</p>
              <p class="persona-preview-devices">Uses: {{devices}}</p>
              <p class="persona-preview-content">Consumes: {{contentTypes}}</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 6: Ad Platform Targeting
      {
        id: 'targeting',
        title: 'Ad Platform Targeting',
        description: 'Define how to target this persona on various advertising platforms.',
        fields: [
          {
            id: 'metaTargeting',
            type: 'markdown',
            label: 'Meta Ads Targeting Strategy',
            description: 'Describe how to target this persona on Facebook, Instagram, etc.',
            placeholder: 'e.g., Detailed targeting combining job titles, interests in tech innovation, and engagement with business content',
            validation: {
              required: false
            },
            templateVar: 'metaTargeting',
            affectsPreview: true
          },
          {
            id: 'googleTargeting',
            type: 'markdown',
            label: 'Google Ads Targeting Strategy',
            description: 'Describe how to target this persona on Google Search, Display, YouTube, etc.',
            placeholder: 'e.g., Search terms related to enterprise software solutions, in-market audiences for business technology, affinity for business leadership',
            validation: {
              required: false
            },
            templateVar: 'googleTargeting',
            affectsPreview: true
          },
          {
            id: 'linkedinTargeting',
            type: 'markdown',
            label: 'LinkedIn Targeting Strategy',
            description: 'Describe how to target this persona on LinkedIn Ads.',
            placeholder: 'e.g., Target by job title, seniority, company size, specific skills, and group memberships',
            validation: {
              required: false
            },
            templateVar: 'linkedinTargeting',
            affectsPreview: true
          },
          {
            id: 'tiktokTargeting',
            type: 'markdown',
            label: 'TikTok Targeting Strategy',
            description: 'Describe how to target this persona on TikTok Ads.',
            placeholder: 'e.g., Interest targeting combined with custom audiences based on app activity and content engagement',
            validation: {
              required: false
            },
            templateVar: 'tiktokTargeting',
            affectsPreview: true
          },
          {
            id: 'customAudiences',
            type: 'markdown',
            label: 'Custom Audience Strategy',
            description: 'Describe strategies for building custom audiences based on this persona.',
            placeholder: 'e.g., Create custom audiences from CRM data, website visitors who viewed enterprise solutions pages, and engagement with specific content types',
            validation: {
              required: false
            },
            templateVar: 'customAudiences',
            affectsPreview: true
          },
          {
            id: 'lookalikeStrategy',
            type: 'markdown',
            label: 'Lookalike/Similar Audience Strategy',
            description: 'Describe how to build and optimize lookalike audiences for this persona.',
            placeholder: 'e.g., Create 1-3% lookalikes from high-value customers matching this persona, use custom value-based lookalikes for ROAS optimization',
            validation: {
              required: false
            },
            templateVar: 'lookalikeStrategy',
            affectsPreview: true
          }
        ],
        template: `
          <div class="buyer-persona-targeting">
            <h3>Ad Platform Targeting</h3>
            <div class="persona-detail">
              <span class="label">Meta Ads Targeting:</span>
              <div class="persona-markdown">{{metaTargeting}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Google Ads Targeting:</span>
              <div class="persona-markdown">{{googleTargeting}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">LinkedIn Targeting:</span>
              <div class="persona-markdown">{{linkedinTargeting}}</div>
            </div>
            {{#if tiktokTargeting}}
            <div class="persona-detail">
              <span class="label">TikTok Targeting:</span>
              <div class="persona-markdown">{{tiktokTargeting}}</div>
            </div>
            {{/if}}
            <div class="persona-detail">
              <span class="label">Custom Audience Strategy:</span>
              <div class="persona-markdown">{{customAudiences}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Lookalike Strategy:</span>
              <div class="persona-markdown">{{lookalikeStrategy}}</div>
            </div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="persona-preview-targeting">
              <p class="persona-preview-meta">Meta: {{metaTargeting}}</p>
              <p class="persona-preview-google">Google: {{googleTargeting}}</p>
              <p class="persona-preview-linkedin">LinkedIn: {{linkedinTargeting}}</p>
            </div>
          `,
          format: 'html'
        }
      },
      
      // Step 7: Optimization & Testing
      {
        id: 'optimization',
        title: 'Optimization & Testing',
        description: 'Define how to optimize and test campaigns targeting this persona.',
        fields: [
          {
            id: 'kpis',
            type: 'multiselect',
            label: 'Key Performance Indicators',
            description: 'Which KPIs are most important for campaigns targeting this persona?',
            validation: {
              required: true
            },
            templateVar: 'kpis',
            affectsPreview: true,
            options: [
              { id: 'ctr', label: 'Click-Through Rate (CTR)', value: 'Click-Through Rate (CTR)' },
              { id: 'conversion', label: 'Conversion Rate', value: 'Conversion Rate' },
              { id: 'cpa', label: 'Cost Per Acquisition (CPA)', value: 'Cost Per Acquisition (CPA)' },
              { id: 'roas', label: 'Return on Ad Spend (ROAS)', value: 'Return on Ad Spend (ROAS)' },
              { id: 'cpc', label: 'Cost Per Click (CPC)', value: 'Cost Per Click (CPC)' },
              { id: 'cpm', label: 'Cost Per Mille (CPM)', value: 'Cost Per Mille (CPM)' },
              { id: 'engagement', label: 'Engagement Rate', value: 'Engagement Rate' },
              { id: 'quality', label: 'Quality Score', value: 'Quality Score' },
              { id: 'reach', label: 'Reach', value: 'Reach' },
              { id: 'frequency', label: 'Frequency', value: 'Frequency' },
              { id: 'impressions', label: 'Impressions', value: 'Impressions' },
              { id: 'viewability', label: 'Viewability', value: 'Viewability' },
              { id: 'video-completion', label: 'Video Completion Rate', value: 'Video Completion Rate' },
              { id: 'ltv', label: 'Customer Lifetime Value (LTV)', value: 'Customer Lifetime Value (LTV)' }
            ]
          },
          {
            id: 'testVariables',
            type: 'multiselect',
            label: 'Test Variables',
            description: 'Which elements should be tested in campaigns targeting this persona?',
            validation: {
              required: false
            },
            templateVar: 'testVariables',
            affectsPreview: true,
            options: [
              { id: 'headlines', label: 'Headlines', value: 'Headlines' },
              { id: 'images', label: 'Images', value: 'Images' },
              { id: 'video', label: 'Videos', value: 'Videos' },
              { id: 'copy', label: 'Ad Copy', value: 'Ad Copy' },
              { id: 'cta', label: 'Call-to-Action', value: 'Call-to-Action' },
              { id: 'landing', label: 'Landing Pages', value: 'Landing Pages' },
              { id: 'offers', label: 'Offers/Promotions', value: 'Offers/Promotions' },
              { id: 'formats', label: 'Ad Formats', value: 'Ad Formats' },
              { id: 'placements', label: 'Ad Placements', value: 'Ad Placements' },
              { id: 'audience', label: 'Audience Segments', value: 'Audience Segments' },
              { id: 'bidding', label: 'Bidding Strategies', value: 'Bidding Strategies' },
              { id: 'schedule', label: 'Ad Scheduling', value: 'Ad Scheduling' }
            ]
          },
          {
            id: 'budgetAllocation',
            type: 'markdown',
            label: 'Budget Allocation',
            description: 'How should budget be allocated across platforms for this persona?',
            placeholder: 'e.g., 40% LinkedIn, 30% Google Search, 20% Meta, 10% Display/Remarketing',
            validation: {
              required: false
            },
            templateVar: 'budgetAllocation',
            affectsPreview: true
          },
          {
            id: 'successMetrics',
            type: 'markdown',
            label: 'Success Metrics',
            description: 'What specific metrics would indicate campaign success with this persona?',
            placeholder: 'e.g., ROAS > 300%, lead-to-opportunity conversion rate > 15%, CAC < $250',
            validation: {
              required: true
            },
            templateVar: 'successMetrics',
            affectsPreview: true
          },  
          {
            id: 'optimizationStrategies',
            type: 'markdown',
            label: 'Optimization Strategies',
            description: 'What strategies should be employed to optimize campaigns for this persona?',
            placeholder: 'e.g., Aggressive bid adjustments for highest-converting demographics, dayparting based on engagement patterns, regular creative refreshes',
            validation: {
              required: false
            },
            templateVar: 'optimizationStrategies',
            affectsPreview: true
          }
        ],
        template: `
          <div class="buyer-persona-optimization">
            <h3>Optimization & Testing</h3>
            <div class="persona-detail">
              <span class="label">Key Performance Indicators:</span> {{kpis}}
            </div>
            <div class="persona-detail">
              <span class="label">Test Variables:</span> {{testVariables}}
            </div>
            <div class="persona-detail">
              <span class="label">Budget Allocation:</span>
              <div class="persona-markdown">{{budgetAllocation}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Success Metrics:</span>
              <div class="persona-markdown">{{successMetrics}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Optimization Strategies:</span>
              <div class="persona-markdown">{{optimizationStrategies}}</div>
            </div>
          </div>
        `,
        preview: {
          enabled: true,
          template: `
            <div class="persona-preview-optimization">
              <p class="persona-preview-kpis">KPIs: {{kpis}}</p>
              <p class="persona-preview-budget">Budget: {{budgetAllocation}}</p>
            </div>
          `,
          format: 'html'
        }
      }
    ],
    output: {
      // The actual export wired in the component is `application/pdf`
      // via `jsPDF` (see `ImageBasedPdfExport.tsx:451-510,851,857`).
      // The data record previously declared `text/html` /
      // `encodingFormat: 'text/html'` on the primary format, which
      // emitted a misleading `CreateAction.result.encodingFormat:
      // text/html` JSON-LD claim through the metadata gate at
      // `src/lib/metadata/resources/metadata.ts:1334`. Aligned the
      // declaration to the actual export shape. From PR #104.
      format: {
        type: 'pdf',
        encodingFormat: 'application/pdf'
      },
      template: `
        <div class="buyer-persona-document">
          <div class="persona-header">
            <h1>{{personaName}}</h1>
            <div class="persona-summary">
              <p class="persona-role">{{jobTitle}} at {{businessType}} {{industry}} Company</p>
              <p class="persona-location">{{location}}</p>
              <p class="persona-demographics">{{gender}}, {{ageRange}}, {{income}}</p>
            </div>
          </div>

          <div class="persona-section">
            <h2>Professional Background</h2>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Job Title:{{else}}Occupation/Role:{{/if}}</span> {{jobTitle}}
            </div>
            <div class="persona-detail">
              <span class="label">Industry:</span> {{industry}}
            </div>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Decision Authority:{{else}}Purchase Authority:{{/if}}</span> {{#if businessType == 'B2B'}}{{b2bDecisionAuthority}}{{else}}{{b2cDecisionAuthority}}{{/if}}
            </div>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Key Responsibilities:{{else}}Daily Activities:{{/if}}</span>
              <div class="persona-markdown">{{#if businessType == 'B2B'}}{{responsibilitiesB2B}}{{else}}{{responsibilitiesB2C}}{{/if}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Education:</span> {{education}}
            </div>
          </div>

          <div class="persona-section">
            <h2>Demographics</h2>
            <div class="persona-detail">
              <span class="label">Age Range:</span> {{ageRange}}
            </div>
            <div class="persona-detail">
              <span class="label">Gender:</span> {{gender}}
            </div>
            <div class="persona-detail">
              <span class="label">Income Level:</span> {{income}}
            </div>
            <div class="persona-detail">
              <span class="label">Location:</span> {{location}}
            </div>
            <div class="persona-detail">
              <span class="label">Family Status:</span> {{familyStatus}}
            </div>
          </div>

          <div class="persona-section">
            <h2>Psychographics</h2>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Professional Values:{{else}}Personal Values:{{/if}}</span> {{#if businessType == 'B2B'}}{{valuesB2B}}{{else}}{{valuesB2C}}{{/if}}
            </div>
            <div class="persona-detail">
              <span class="label">Interests:</span> {{interests}}
            </div>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Professional Goals:{{else}}Personal Goals:{{/if}}</span>
              <div class="persona-markdown">{{#if businessType == 'B2B'}}{{goalsB2B}}{{else}}{{goalsB2C}}{{/if}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">{{#if businessType == 'B2B'}}Professional Challenges:{{else}}Personal Challenges:{{/if}}</span>
              <div class="persona-markdown">{{#if businessType == 'B2B'}}{{challengesB2B}}{{else}}{{challengesB2C}}{{/if}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Pain Points:</span>
              <div class="persona-markdown">{{painPoints}}</div>
            </div>
          </div>

          <div class="persona-section">
            <h2>Buying Behavior</h2>
            <div class="persona-detail">
              <span class="label">Research Methods:</span> {{researchMethods}}
            </div>
            <div class="persona-detail">
              <span class="label">Purchase Frequency:</span> {{purchaseFrequency}}
            </div>
            <div class="persona-detail">
              <span class="label">Decision Criteria:</span> {{decisionCriteria}}
            </div>
            <div class="persona-detail">
              <span class="label">Preferred Communication:</span> {{communicationChannels}}
            </div>
            <div class="persona-detail">
              <span class="label">Brand Loyalty Factors:</span>
              <div class="persona-markdown">{{brandLoyalty}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Budget Consciousness:</span> {{budgetConsciousness}}
            </div>
          </div>

          <div class="persona-section">
            <h2>Digital Presence</h2>
            <div class="persona-detail">
              <span class="label">Preferred Platforms:</span> {{platforms}}
            </div>
            <div class="persona-detail">
              <span class="label">Device Usage:</span> {{devices}}
            </div>
            <div class="persona-detail">
              <span class="label">Content Consumption:</span> {{contentTypes}}
            </div>
            <div class="persona-detail">
              <span class="label">Online Behavior:</span>
              <div class="persona-markdown">{{onlineBehavior}}</div>
            </div>
            {{#if digitalInfluence}}
            <div class="persona-detail">
              <span class="label">Digital Influence:</span> {{digitalInfluence}}
            </div>
            {{/if}}
          </div>

          <div class="persona-section">
            <h2>Ad Platform Targeting</h2>
            <div class="persona-detail">
              <span class="label">Meta Ads Targeting:</span>
              <div class="persona-markdown">{{metaTargeting}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Google Ads Targeting:</span>
              <div class="persona-markdown">{{googleTargeting}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">LinkedIn Targeting:</span>
              <div class="persona-markdown">{{linkedinTargeting}}</div>
            </div>
            {{#if tiktokTargeting}}
            <div class="persona-detail">
              <span class="label">TikTok Targeting:</span>
              <div class="persona-markdown">{{tiktokTargeting}}</div>
            </div>
            {{/if}}
            <div class="persona-detail">
              <span class="label">Custom Audience Strategy:</span>
              <div class="persona-markdown">{{customAudiences}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Lookalike Strategy:</span>
              <div class="persona-markdown">{{lookalikeStrategy}}</div>
            </div>
          </div>

          <div class="persona-section">
            <h2>Campaign Optimization</h2>
            <div class="persona-detail">
              <span class="label">Key Performance Indicators:</span> {{kpis}}
            </div>
            <div class="persona-detail">
              <span class="label">Test Variables:</span> {{testVariables}}
            </div>
            <div class="persona-detail">
              <span class="label">Budget Allocation:</span>
              <div class="persona-markdown">{{budgetAllocation}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Success Metrics:</span>
              <div class="persona-markdown">{{successMetrics}}</div>
            </div>
            <div class="persona-detail">
              <span class="label">Optimization Strategies:</span>
              <div class="persona-markdown">{{optimizationStrategies}}</div>
            </div>
          </div>

          <div class="persona-footer">
            <p>Generated with AdSights Buyer Persona Generator | {{formatDate now 'YYYY-MM-DD'}}</p>
          </div>
        </div>
      `,
      filename: '{{personaName}}-Buyer-Persona-{{formatDate now "YYYY-MM-DD"}}.html'
    },
    ui: {
      enablePreview: true,
      enableDownload: true,
      previewDelay: 500
    }
  },
  faqs: [
    {
      question: "What is a buyer persona and why is it important?",
      answer: "A buyer persona is a detailed, semi-fictional profile of your ideal customer based on market research, data analysis, and real customer insights. It's crucial for digital marketing because it helps align marketing, sales, and product teams around a shared understanding of target customers, enables more precise ad targeting and higher ROI on marketing spend, informs content strategy and messaging to better resonate with your audience, guides product development and feature prioritization, improves customer service by anticipating needs, and allows for more personalized marketing automation and nurture flows."
    },
    {
      question: "What information should I include in a buyer persona?",
      answer: "A comprehensive buyer persona should include core demographics such as age range, gender, location, income level, education, career path, and family status. You'll also want to capture psychographic details including values, beliefs, lifestyle choices, hobbies, interests, and professional goals. Important behavioral insights include their purchase decision-making process, brand preferences, price sensitivity, and technology adoption patterns. For digital marketing purposes, document their digital footprint including preferred social media platforms, content consumption habits, device usage, and online shopping behaviors. Finally, include marketing-specific data like platform targeting parameters, custom audience characteristics, engagement patterns, and response to different marketing messages."
    },
    {
      question: "How do I research information for my buyer persona?",
      answer: "Effective buyer persona research requires analyzing multiple data sources including Google Analytics demographic and behavior data, social media analytics, CRM data, email marketing engagement metrics, and ad platform audience insights. Complement quantitative data with qualitative research through customer interviews, focus groups, sales team feedback, call recordings, and social media listening. Conduct competitive analysis by studying competitor audiences, industry reports, and market studies. Gather direct customer feedback through surveys, website feedback tools, product usage analytics, and customer reviews. The key is triangulating multiple data sources to build a complete picture of your ideal customer."
    },
    {
      question: "How often should I update my buyer personas?",
      answer: "Buyer personas should be treated as living documents that evolve with your business and market conditions. Implement quarterly light reviews to check for emerging trends, with deeper annual analysis and updates. Monitor for trigger events that necessitate immediate updates such as significant changes in customer behavior, new product launches, market expansion, competitive landscape changes, economic disruptions, technology adoption shifts, or changes in marketing channel performance. Track persona accuracy through ongoing campaign performance analysis, sales team feedback, and customer interaction monitoring. The goal is to maintain personas that accurately reflect current market conditions and customer behaviors."
    },
    {
      question: "How do I create a comprehensive buyer persona?",
      answer: "Work through the persona layer by layer, the way this generator's sections are organized: start with business context (your product, market, and goals), then basic identity and demographics, then psychographics (values, motivations, pain points), behavior and buying patterns, digital footprint (platforms, content habits, devices), and finally ad-platform targeting parameters you can act on in Meta, LinkedIn, TikTok, or Google Ads. A persona is comprehensive when every section is grounded in real inputs — analytics, CRM data, customer interviews, and sales feedback — rather than guesses, so validate each field against at least one data source before you rely on it for targeting."
    },
    {
      question: "How can I use buyer personas for ad targeting?",
      answer: "Buyer personas directly inform platform-specific targeting strategies across Facebook, LinkedIn, TikTok, and Google Ads. Use demographic and interest data to create Custom Audiences, target by professional attributes, and develop keyword strategies based on persona pain points. Implement advanced targeting by building lookalike audiences from your best customers, creating exclusion audiences to prevent waste, developing sequential targeting strategies, and implementing cross-platform attribution tracking. Optimize content by tailoring ad creative, messaging, and landing pages to each persona's preferences and funnel stage. Strategically allocate budget by prioritizing top-performing segments, testing new channels based on persona behavior, and adjusting bids based on customer lifetime value calculations."
    }
  ],
  relatedResources: [
    {
      id: "buyer-persona-template",
      title: "Buyer Persona Template for Paid Media",
      relationshipDescription: "Download our comprehensive buyer persona PDF or Canva template",
      slug: "digital-marketing-buyer-persona-template",
      fullRoute: "/resources/templates/digital-marketing-buyer-persona-template",
      resourceType: "template"
    },
    {
      id: "customer-ltv-calculator",
      title: "Customer LTV Calculator",
      relationshipDescription: "Calculate customer lifetime value to inform persona targeting",
      slug: "customer-ltv-calculator",
      fullRoute: "/resources/tools/calculators/customer-ltv-calculator",
      resourceType: "tool",
      toolType: "calculator"
    },
    {
      id: "audience-segmentation",
      title: "Audience Segmentation Glossary Term",
      relationshipDescription: "Master audience segmentation strategies",
      slug: "audience-segmentation",
      fullRoute: "/resources/glossary/general/audience-segmentation",
      resourceType: "glossary-term"
    },
    {
      id: "persona-development",
      title: "Persona Development Glossary Term",
      relationshipDescription: "Learn best practices for creating effective buyer personas",
      slug: "persona-development",
      fullRoute: "/resources/glossary/creative/persona-development",
      resourceType: "glossary-term"
    }
  ]
} 